import { Injectable } from '@angular/core';
import { AjaxService } from './ajax.service';
import { AuthService } from './auth.service';
import { BreadcrumbService } from './breadcrumb.service';
import { RelatedServiceInterface } from '../interfaces/related-service.interface';
import { ServiceInterface } from '../interfaces/service.interface';
import { ServiceDetailsInterface } from '../interfaces/service-details.interface';
import { APP_ROUTES } from '../constants/app-routes.constant';
import { CHANNEL } from '../constants/channel.constant';
import { ENDPOINTS } from '../constants/endpoints.constant';
import { SERVICE_BREADCRUMB_LABEL } from '../constants/access-service-breadcrumb-label.constant';
import { CategoryServiceInterface } from '../interfaces/category-service.interface';

@Injectable()
export class ServicesService {
  constructor(
    private ajaxService: AjaxService,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService
  ) {}

  private getServicesWithUrls(
    services: ServiceInterface[] | RelatedServiceInterface[]
  ): ServiceInterface[] | RelatedServiceInterface[] {
    const servicesWithUrls = services.map((item: ServiceInterface & RelatedServiceInterface) => {
      const acessar =
        this.authService.isLoggedIn() && !item?.urlServico ? APP_ROUTES.services.subChildren.accessService.url : '';

      const url = `${APP_ROUTES.services.url}/${item.id}/${acessar}`;

      return {
        ...item,
        url
      };
    });

    return servicesWithUrls;
  }

  private handleEmptyServicesListMessage(servicesList: ServiceInterface[]) : boolean  {
    if (servicesList && servicesList.length && servicesList[0] && servicesList[0].message) {
      return true;
    }

    return false;
  }

  public setAccessedServiceBreadcrumb(service: ServiceInterface) : void {
    if (service) {
      this.breadcrumbService.updateDynamicBreadcrumbs(SERVICE_BREADCRUMB_LABEL, service.nome);
    }
  }

  public async getAllServices() : Promise<ServiceInterface[]>  {
    let allServices = await this.ajaxService.get<ServiceInterface[]>(ENDPOINTS.allServices.url);

    const servicesListIsEmpty = this.handleEmptyServicesListMessage(allServices);

    if (servicesListIsEmpty) {
      return [];
    }

    allServices = this.getServicesWithUrls(allServices) as ServiceInterface[];

    return allServices;
  }

  public async getChannelServices() : Promise<ServiceInterface[]>  {
    let channelServices = await this.ajaxService.get<ServiceInterface[]>(`${ENDPOINTS.channelServices.url}/${CHANNEL}`);

    const servicesListIsEmpty = this.handleEmptyServicesListMessage(channelServices);

    if (servicesListIsEmpty) {
      return [];
    }

    channelServices = this.getServicesWithUrls(channelServices) as ServiceInterface[];

    return channelServices;
  }

  public async getMostFrequentServices() : Promise<ServiceInterface[]>  {
    let mostFrequentServices = await this.ajaxService.get<ServiceInterface[]>(
      `${ENDPOINTS.mostFrequentServices.url}/${CHANNEL}`
    );

    const servicesListIsEmpty = this.handleEmptyServicesListMessage(mostFrequentServices);

    if (servicesListIsEmpty) {
      return [];
    }

    mostFrequentServices = this.getServicesWithUrls(mostFrequentServices) as ServiceInterface[];

    return mostFrequentServices;
  }

  public getServiceById(serviceId: number) : Promise<ServiceInterface>  {
    return this.ajaxService.get<ServiceInterface>(`${ENDPOINTS.serviceById.url}/${serviceId}`);
  }

  public async getServiceByIdNoFields(serviceId: number) : Promise<any> {
    const response = await this.ajaxService.get<ServiceInterface>(`${ENDPOINTS.allServices.url}/${serviceId}`);

    return response && response[0];
  }

  public async getServiceDetails(serviceId: number) : Promise<ServiceDetailsInterface> {
    const serviceDetails = await this.ajaxService.get<ServiceDetailsInterface>(
      `${ENDPOINTS.serviceDetails.url}/${serviceId}`
    );

    return serviceDetails;
  }

  public async getRelatedServices(serviceId: number) : Promise<RelatedServiceInterface[]> {
    let relatedServices = await this.ajaxService.get<RelatedServiceInterface[]>(
      `${ENDPOINTS.relatedServices.url}/${serviceId}`
    );

    relatedServices = this.getServicesWithUrls(relatedServices) as RelatedServiceInterface[];

    return relatedServices;
  }

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
  public executeService(body: any, returnType  = 'body')  : Promise<any>{
    const userAccessToken = this.authService.getUserAccessToken();
  
    let notAuth = false;
    let token = null;
  
    if(userAccessToken != null){
      token = {
         Authorization_lc: `Bearer ${userAccessToken}`
      };
    } else {
      notAuth = true;
    }
  

    return this.ajaxService.post<any>(
      ENDPOINTS.executeService.url,
      body,
      notAuth,
      token,
      returnType
    );

  }

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
  public executeServiceStatic(body: any, returnType = 'body') : Promise<any> {
    const userAccessToken = this.authService.getUserAccessToken();
  
    let notAuth = false;
    let token = null;
  
    if(userAccessToken != null){
      token = {
         Authorization_lc: `Bearer ${userAccessToken}`
      };
    } else {
      notAuth = true;
    }
  

    return this.ajaxService.post<any>(
      ENDPOINTS.executeServiceStatic.url,
      body,
      notAuth,
      token,
      returnType
    );

  }

  public async getServicesByCategory(categoryCode: number): Promise<CategoryServiceInterface | null> {
    const services = await this.ajaxService.get<CategoryServiceInterface>(
      `${ENDPOINTS.categoryServices.url}/${categoryCode}/${CHANNEL}`
    );
  
    if (!services || !services.tiposSolicitacao) {
      return null;
    }
  
    const servicesWithUrl = this.getServicesWithUrls(services.tiposSolicitacao) as ServiceInterface[];
    services.tiposSolicitacao = servicesWithUrl;
  
    return services;
  }

}
